import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {BackofficeService, SearchBackofficeCriteriaDto, SearchBackofficeDto} from '../../../_services/configuration-services';
import {EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, tap} from 'rxjs/operators';
import {NgbTypeaheadSelectItemEvent} from '@ng-bootstrap/ng-bootstrap';
import {faEraser} from '@fortawesome/free-solid-svg-icons';
import {FormUtils} from '../../form-utils';

@Component({
    selector: 'app-search-backoffice',
    templateUrl: './search-backoffice.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchBackofficeComponent implements OnInit {

    // Only backoffices holding at least one of these permissions are proposed.
    // The hosting component decides which permissions are relevant (e.g. the alert/alarm
    // history "released by" filter passes GestionAlertesAlarmes + ConfigurationAlertesAlarmes).
    @Input() permissionKeys: string[] = [];
    @Input() defaultBackoffice: SearchBackofficeDto | null = null;
    @Input() disabled = false;

    @Output() selectedBackofficeEvent = new EventEmitter<SearchBackofficeDto | null>();

    searchSubject$ = new Subject<string>();

    searchText: string | null = null;
    selectedBackoffice: SearchBackofficeDto | null = null;
    printedSelectedBackoffice: string | null = null;

    textInputPattern = FormUtils.textInputPattern;

    searchFailed = false;

    icons = {
        erase: faEraser
    };

    formatter = (backoffice: SearchBackofficeDto | null) => backoffice ? `${backoffice.lastName} ${backoffice.firstName}` : '';

    search = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(
            debounceTime(200),
            distinctUntilChanged());

        return merge(debouncedText$, this.searchSubject$).pipe(
            switchMap(term => {
                // On focus / empty input, still query (with an empty term) so the dropdown
                // lists all matching backoffices
                if (term.length < 1) {
                    this.selectBackoffice(null);
                }

                // Skip re-querying only when the field already shows the selected item (non-empty term).
                // An empty term must always re-query so the default list reappears — including right after
                // the eraser button, which resets the printed value to '' .
                if (term.length >= 1 && this.printedSelectedBackoffice === term) {
                    return EMPTY;
                }

                return this.backofficeService.searchBackoffices(new SearchBackofficeCriteriaDto({
                    searchTerm: term,
                    permissionKeys: this.permissionKeys
                }))
                    .pipe(tap(res => {
                            if (res == null || res.length === 0) {
                                this.searchFailed = true;
                                this.selectBackoffice(null);
                                return of([]);
                            }
                            this.searchFailed = false;
                            return of([]);
                        }),
                        catchError(() => {
                            this.searchFailed = true;
                            this.selectBackoffice(null);
                            return of([]);
                        }));
            }));
    }

    constructor(private readonly backofficeService: BackofficeService) {
    }

    ngOnInit(): void {
        this.selectBackoffice(this.defaultBackoffice);
    }

    selectItem(selectItemEvent: NgbTypeaheadSelectItemEvent<SearchBackofficeDto>): void {
        this.selectBackoffice(selectItemEvent.item);
    }

    public setSelectedBackoffice(backoffice: SearchBackofficeDto | null): void {
        this.selectBackoffice(backoffice);
    }

    public clearInput(): Array<SearchBackofficeDto> {
        this.searchFailed = false;
        this.selectBackoffice(null, true);
        return [];
    }

    private selectBackoffice(backoffice: SearchBackofficeDto | null, force = false): void {
        if (backoffice || force) {
            this.selectedBackoffice = backoffice;
            this.printedSelectedBackoffice = this.formatter(backoffice);
            this.selectedBackofficeEvent.emit(backoffice);

        } else {
            this.selectedBackofficeEvent.emit(null);
        }
    }
}
